import { AxiosError } from 'axios'
import { SelectProps } from 'components/Select'
import { ENDPOINTS } from 'constants/endpoints'
import apiInstance from 'services/api/instance'

import { handleErrorMessage } from '../errors'

export const getAll = async (): Promise<ActivityApiResponse[]> => {
  try {
    const getAllResponse = await apiInstance.get<ActivityApiResponse[]>(
      ENDPOINTS.ACTIVITIES.GET_ALL,
    )

    return getAllResponse.data
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const getAllCalendar = async (): Promise<ActivityApiResponse[]> => {
  try {
    const getAllResponse = await apiInstance.get<ActivityApiResponse[]>(
      ENDPOINTS.ACTIVITIES.GET_ALL_CALENDAR,
    )

    return getAllResponse.data
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const getAllCalendarByPeriod = async (
  startPeriod: string,
  endPeriod: string,
): Promise<ActivityApiResponse[]> => {
  try {
    const getAllResponse = await apiInstance.get<ActivityApiResponse[]>(
      ENDPOINTS.ACTIVITIES.GET_ALL_CALENDAR_PERIOD(startPeriod, endPeriod),
    )

    return getAllResponse.data
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const get = async (id: string): Promise<ActivityApiResponse> => {
  try {
    const getResponse = await apiInstance.get<ActivityApiResponse>(
      ENDPOINTS.ACTIVITIES.GET(id),
    )

    return {
      ...getResponse.data,
      budget_id: getResponse.data.original_activity_id
        ? 0
        : getResponse.data.budget_id,
      original_budget_id: getResponse.data.budget_id,
    }
  } catch (getError) {
    const axiosError = getError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const getAllNamesAndIds = async (): Promise<SelectProps['options']> => {
  try {
    const getAllResponse = await apiInstance.get<ActivityApiResponse[]>(
      ENDPOINTS.ACTIVITIES.GET_OPTIONS,
    )
    return (getAllResponse.data || []).map((activity) => ({
      label: `${activity.code} - ${activity.name}`,
      value: activity.id.toString(),
    }))
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const create = async (
  activity: ActivityApiRequest,
): Promise<ActivityApiResponse> => {
  try {
    const creationResponse = await apiInstance.post<ActivityApiResponse>(
      ENDPOINTS.ACTIVITIES.CREATE,
      activity,
    )

    return creationResponse.data
  } catch (creationError) {
    const axiosError = creationError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const update = async ({
  id,
  activity,
}: {
  activity: ActivityApiRequest
  id: string
}): Promise<ActivityApiResponse> => {
  try {
    const updateResponse = await apiInstance.put<ActivityApiResponse>(
      ENDPOINTS.ACTIVITIES.UPDATE(id),
      activity,
    )

    return updateResponse.data
  } catch (updateError) {
    const axiosError = updateError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const updateInvoiceStatus = async ({
  id,
}: {
  id: string
}): Promise<{ success: boolean; message: string }> => {
  try {
    const updateResponse = await apiInstance.put<{
      success: boolean
      message: string
    }>(ENDPOINTS.ACTIVITIES.UPDATE_INVOICE_STATUS(id))

    return updateResponse.data
  } catch (updateError) {
    const axiosError = updateError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const remove = async (id: string) => {
  try {
    const deleteResponse = await apiInstance.delete(
      ENDPOINTS.ACTIVITIES.DELETE(id),
    )

    return deleteResponse.data
  } catch (deleteError) {
    const axiosError = deleteError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendGuideMail = async (responsible_guide_activity_id: string) => {
  try {
    const sendMailResponse = await apiInstance.post(
      ENDPOINTS.ACTIVITIES.SEND_GUIDE_MAIL(responsible_guide_activity_id),
    )

    return sendMailResponse.data
  } catch (sendMailError) {
    const axiosError = sendMailError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendCertMail = async (id: string) => {
  try {
    const sendMailResponse = await apiInstance.post(
      ENDPOINTS.ACTIVITIES.SEND_CERT_MAIL(id),
    )

    return sendMailResponse.data
  } catch (sendMailError) {
    const axiosError = sendMailError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendPreReservationEmailAndWpp = async (id: string) => {
  try {
    const sendMailResponse = await apiInstance.post(
      ENDPOINTS.ACTIVITIES.SEND_PRE_RESERVATION_EMAIL_AND_WPP(id),
    )

    return sendMailResponse.data
  } catch (sendMailError) {
    const axiosError = sendMailError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendMessage = async ({
  id,
  message,
  situation,
}: ActivitySendMessageRequest) => {
  try {
    const sendMessageResponse = await apiInstance.post(
      ENDPOINTS.ACTIVITIES.SEND_MESSAGE,
      {
        id,
        message,
        situation,
      },
    )
    return sendMessageResponse.data
  } catch (sendMessageError) {
    const axiosError = sendMessageError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendMailConfirmationTeachers = async ({
  id,
}: ActivitySendMailConfirmationTeachersRequest) => {
  try {
    const sendMessageResponse = await apiInstance.post(
      ENDPOINTS.ACTIVITIES.SEND_MAIL_CONFIRMATION_TEACHERS,
      {
        id,
      },
    )
    return sendMessageResponse.data
  } catch (sendMessageError) {
    const axiosError = sendMessageError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendMailAlbumLink = async ({
  id,
}: ActivitySendMailAlbumLinkRequest) => {
  try {
    const sendMessageResponse = await apiInstance.post(
      ENDPOINTS.ACTIVITIES.SEND_MAIL_ALBUM_LINK,
      {
        id,
      },
    )
    return sendMessageResponse.data
  } catch (sendMessageError) {
    const axiosError = sendMessageError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}
